import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { adminApi, ADMIN_TOKEN_KEY } from "./adminApi";

const Ctx = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(ADMIN_TOKEN_KEY) || "");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(!!token);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await adminApi.get("/me");
      setMe(data);
      return data;
    } catch {
      setMe(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let on = true;
    if (token) {
      setLoading(true);
      refreshMe().finally(() => { if (on) setLoading(false); });
    } else {
      setMe(null);
      setLoading(false);
    }
    return () => { on = false; };
  }, [token, refreshMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await adminApi.post("/login", { email, password });
    localStorage.setItem(ADMIN_TOKEN_KEY, data.token);
    setToken(data.token);
    setMe({ email: data.email, must_change: data.must_change });
    return data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken("");
    setMe(null);
  }, []);

  const changePassword = useCallback(async (newPassword, currentPassword = "") => {
    await adminApi.post("/change-password", { new_password: newPassword, current_password: currentPassword });
    await refreshMe();
  }, [refreshMe]);

  return (
    <Ctx.Provider value={{ token, me, loading, authed: !!token, mustChange: !!me?.must_change, login, logout, changePassword, refreshMe }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAdminAuth = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAdminAuth must be used within AdminAuthProvider");
  return c;
};
